import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import has from '@dojo/framework/core/has';
import { LoadingIndicator } from '@dojo/widgets/loading-indicator';
import { createNlsMiddleware, documentapi, userstate, lccstate, recommended } from 'lcc-state';
import { publish } from '@holmescorp/pub-sub';
import { ScoreChart } from '@holmescorp/progress-chart';
import css from '../../css/progress-view.m.css';
import { MicroCardButton, svc, getTestResultScore, Hero, ScoreToast } from '../index';
const factory = create({
    icache: createICacheMiddleware(),
    nls: createNlsMiddleware('ditabook'),
    settingsNls: createNlsMiddleware('settings'),
    theme,
    userstate,
    lccstate,
    documentapi,
    recommended
});
export const FinalTestPanel = factory(({ middleware: { icache, theme, userstate, nls, lccstate, settingsNls, recommended } }) => {
    const { messages: { practice_type = 'practice', card_subtitle_quiz_start = '[ditabook] card_subtitle_quiz_start', assess_progress_threshold_high = '80', use_progress_chart_color = 'false', final_test_not_started_label = "You haven't taken this test yet.", final_test_threshold_low_label = "We've highlighted this as a priority to revisit.", final_test_threshold_mid_label = "We've highlighted this as a priority to revisit.", final_test_threshold_high_label = 'You passed this test.', has_pretest = 'false', micro_pretest_label = 'Take Pre-test', micro_review_pretest_label = 'Review Pre-test' } } = nls();
    const { messages: { title: product_title = '' } } = settingsNls();
    const alertMessages = {
        none: final_test_not_started_label,
        low: final_test_threshold_low_label,
        mid: final_test_threshold_mid_label,
        high: final_test_threshold_high_label
    };
    const { get: uget, path: upath } = userstate;
    const { portal_site: site, portal_userid: userid, product: product_id, course_session } = window.hcstatic;
    const session_id = course_session && course_session.pk;
    const themeCss = theme.classes(css);
    const passingScore = parseInt(assess_progress_threshold_high) || 80;
    const useProgressChartColor = use_progress_chart_color === 'true';
    const testResults = icache.getOrSet('test_results', async () => {
        const member_id = userid || '';
        const data = await svc.attempts_quizzes({
            quiz_type: 'practice',
            member_id,
            product_id,
            session_id,
            site
        });
        return data.reduce((groupedResults, result) => {
            const scores = result.attempts
                .map((attempt) => attempt.score)
                .filter((score) => score !== null && score !== undefined && score !== '');
            groupedResults[result.kind] = scores.map((score) => ({ score }));
            return groupedResults;
        }, {});
    });
    const get_tree = (practice_type) => {
        const practiceResults = uget(upath('practice'));
        const posttestResults = uget(upath('posttest'));
        switch (practice_type) {
            case 'practice':
                return practiceResults;
            case 'posttest':
                return posttestResults.tree;
        }
        return {};
    };
    const posttestTree = get_tree(practice_type);
    const ditaroots = lccstate.get(lccstate.path('ditaroots'));
    const fullPosttest = uget(upath('posttest'));
    const pretestTree = uget(upath('assessment'));
    const hasPretest = has_pretest === 'true';
    const { score: posttestScore, hasTestResults: hasPosttestResults } = getTestResultScore(posttestTree, ditaroots, practice_type, fullPosttest);
    const { score: pretestScore, hasTestResults: hasPretestResults } = getTestResultScore(pretestTree, ditaroots, 'pretest');
    const hasCompletedPretest = recommended.assessmentsComplete();
    const getHeroContent = (score, scoreHistory) => {
        const hasPassed = score >= passingScore;
        const badgeExtension = has('use-png-badge-logo') ? '.png' : '.svg';
        const bestFinalTestScore = scoreHistory[practice_type]
            ? scoreHistory[practice_type]
                .map((scoreHistory) => parseFloat(scoreHistory.score))
                .filter((score) => !isNaN(score))
                .reduce((max, score) => Math.max(max, score), 0)
            : 0;
        const bestPretestScore = scoreHistory['pretest']
            ? scoreHistory['pretest']
                .map((scoreHistory) => parseFloat(scoreHistory.score))
                .filter((score) => !isNaN(score))
                .reduce((max, score) => Math.max(max, score), 0)
            : 0;
        return w(Hero, { additionalClasses: [themeCss.hero] }, [
            v('img', {
                src: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/badge${badgeExtension}`,
                alt: `${product_title} Logo Badge`,
                classes: [themeCss.badge]
            }),
            v('div', { classes: [themeCss.center], xqa: 'progress-view-hero-center' }, [
                v('h3', { classes: [themeCss.label] }, [product_title]),
                w(ScoreToast, { score, messages: alertMessages }),
                hasPretest
                    ? w(MicroCardButton, {
                        labelText: hasCompletedPretest ? micro_review_pretest_label : micro_pretest_label,
                        showIcon: true,
                        darkMode: true,
                        xqa: 'progress-view-pretest-button',
                        additionalClasses: [themeCss.quizButton],
                        onClick: () => {
                            publish(`site/assessment`, 'pre-test');
                        },
                        onDown: () => {
                            publish(`site/assessment`, 'pre-test');
                        }
                    })
                    : null,
                w(MicroCardButton, {
                    labelText: card_subtitle_quiz_start,
                    showIcon: true,
                    darkMode: !hasPassed,
                    disabled: hasPassed,
                    xqa: 'progress-view-test-button',
                    additionalClasses: [themeCss.quizButton],
                    onClick: () => {
                        publish(`site/${practice_type}`);
                    },
                    onDown: () => {
                        publish(`site/${practice_type}`);
                    }
                })
            ]),
            hasCompletedPretest && hasPretestResults
                ? v('div', { classes: [themeCss.trailing], xqa: 'progress-view-pretest-score-chart' }, [
                    w(ScoreChart, {
                        score: pretestScore,
                        bestScore: bestPretestScore,
                        useBestScoreIcon: false,
                        bestScoreText: 'Best Pre-test Score',
                        useInsideLabel: true,
                        insideLabelText: 'Test Score',
                        useScoreColor: useProgressChartColor,
                        additionalClasses: [themeCss.small]
                    })
                ])
                : v('div', { xqa: 'progress-view-no-score-chart' }, []),
            hasPosttestResults
                ? v('div', { classes: [themeCss.trailing], xqa: 'progress-view-score-chart' }, [
                    w(ScoreChart, {
                        score: posttestScore,
                        bestScore: bestFinalTestScore,
                        useBestScoreIcon: false,
                        bestScoreText: practice_type === 'practice' ? 'Best Practice Exam Score' : 'Best Post-test Score',
                        useInsideLabel: true,
                        insideLabelText: 'Test Score',
                        useScoreColor: useProgressChartColor,
                        additionalClasses: [themeCss.small]
                    })
                ])
                : v('div', { xqa: 'progress-view-no-score-chart' }, [])
        ]);
    };
    return testResults
        ? getHeroContent(posttestScore, testResults)
        : w(LoadingIndicator, { active: true, type: 'circular-medium' });
});
export default FinalTestPanel;
